import React from 'react';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import aiAvatarImg from '@/assets/images/ip-logo100X100.png';
import type { ChatMessage, RunConfig } from '../../index';
import JsonAnswerView from '../shared/JsonAnswerView';
import RunConfigSummary from '../shared/RunConfigSummary';

interface Props {
  config: RunConfig | null;
  messages: ChatMessage[];
  onBackToList: () => void;
  onViewProcess?: () => void;
  canViewProcess?: boolean;
}

const ReadonlyResultView: React.FC<Props> = ({
  config,
  messages,
  onBackToList,
  onViewProcess,
  canViewProcess = false,
}) => {
  const firstUserMessage = messages.find(msg => msg.role === 'user');
  const lastAssistantMessageIndex = messages.reduce(
    (lastIndex, msg, index) => (msg.role === 'assistant' ? index : lastIndex),
    -1
  );

  return (
    <div className="flex h-full min-h-0">
      <div className="thin-scrollbar w-[240px] shrink-0 overflow-y-auto border-0 border-r border-solid border-[#d1d5db] bg-white px-5 py-5">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2094f0] text-white">
            <UserOutlined style={{ fontSize: 14 }} />
          </div>
          <span className="text-sm font-medium text-[#111827]">User Name</span>
        </div>
        {firstUserMessage?.content && (
          <p
            className="mb-5 overflow-hidden text-sm leading-relaxed text-[#111827]"
            title={firstUserMessage.content}
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 6,
            }}
          >
            {firstUserMessage.content}
          </p>
        )}
        {config?.createdAt && (
          <p className="mb-5 text-sm text-[#98a2b3]">{config.createdAt}</p>
        )}
        {config && <RunConfigSummary config={config} />}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#eef4f8]">
        <div className="relative min-h-0 flex-1 overflow-y-auto px-8 py-7">
          <button
            type="button"
            onClick={onBackToList}
            className="absolute left-8 top-6 flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-[#d0d5dd] bg-white text-[#344054] transition-colors hover:border-[#98a2b3] hover:text-[#101828]"
            aria-label="返回任务列表"
          >
            <ArrowLeftOutlined />
          </button>

          <div className="mx-auto max-w-[840px] space-y-6 pt-12">
            {messages.map((msg, idx) => (
              <div key={idx}>
                {msg.role === 'user' ? (
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2094f0] text-white">
                      <UserOutlined
                        style={{
                          fontSize: 13,
                          transform: 'translate(-0.15px, -1px)',
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="my-0 text-sm font-medium leading-snug text-[#111827]">
                        {msg.content}
                      </p>
                      {msg.time && (
                        <p className="mt-1 text-xs text-[#9ca3af]">
                          {msg.time}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <img
                      src={aiAvatarImg}
                      alt="AI"
                      className="mt-0.5 h-7 w-7 shrink-0 rounded-full object-contain"
                    />
                    <div className="flex-1">
                      {msg.displayMode === 'json' ? (
                        <JsonAnswerView value={msg.structuredContent} />
                      ) : (
                        <p className="my-0 whitespace-pre-wrap text-sm leading-snug text-[#374151]">
                          {msg.content}
                        </p>
                      )}
                      {canViewProcess && idx === lastAssistantMessageIndex && (
                        <button
                          type="button"
                          onClick={onViewProcess}
                          className="mt-3 cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-[#2094f0] transition-colors hover:text-[#1a7fd4]"
                        >
                          查看运行过程
                        </button>
                      )}
                      {msg.time && (
                        <p className="mt-1 text-xs text-[#9ca3af]">
                          {msg.time}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadonlyResultView;
